import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ArrowRight, AlertTriangle, BarChart3, Package, ShoppingBag, FileText } from "lucide-react";
import { api } from "../lib/api";

const TYPE_ICONS = {
  low_stock: AlertTriangle,
  daily_report: BarChart3,
  packaging_request: Package,
  packaging_approved: Package,
  packaging_rejected: Package,
  packaging_fulfilled: Package,
  order: ShoppingBag,
  invoice: FileText,
};

const SEVERITY_TONES = {
  high: "bg-destructive/10 text-destructive",
  medium: "bg-[hsl(var(--accent-gold-soft))] text-[hsl(var(--primary))]",
  low: "bg-secondary text-muted-foreground",
};

function relativeTime(iso) {
  const d = new Date(iso); const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export default function NotificationBell({ variant = "sidebar" }) {
  const [data, setData] = useState({ count: 0, unread: 0, high_count: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/notifications?limit=10").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);  // faster polling for real-time feel
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openNotification = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.warn("[NotificationBell] mark-read failed", e?.message || e); }
    }
    setOpen(false);
    if (n.link) {
      // Order links → open the order details popup via URL param `?order=<id>`
      // (Each portal listens to this param and shows the OrderDetailsDialog.)
      if (n.type === "order" && n.link.startsWith("/orders")) {
        // Attempt to extract the order id from the message if present, else route
        // to the default location. In practice most `order` notifs link to /orders.
        navigate(n.link);
      } else {
        navigate(n.link);
      }
    }
    load();
  };

  const badgeCount = data.unread;
  const hasUnread = badgeCount > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`notifications-bell-${variant}`}
        className="relative h-10 w-10 rounded-full border border-border bg-white hover:bg-secondary flex items-center justify-center transition-all"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-foreground" strokeWidth={1.5} />
        {hasUnread && (
          <>
            <span
              data-testid="notifications-badge"
              className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                data.high_count > 0 ? "bg-destructive text-white" : "bg-primary text-primary-foreground"
              }`}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
            <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full pulse-dot ${
              data.high_count > 0 ? "bg-destructive/40" : "bg-primary/40"
            }`} aria-hidden />
          </>
        )}
      </button>

      {open && (
        <>
          {/* Subtle backdrop so the popover reads as a true "popup in the frame" */}
          <div
            className="fixed inset-0 z-[60] bg-foreground/10 backdrop-blur-[1.5px]"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            /* Fixed to the viewport so it's never clipped by the sidebar / any parent overflow.
               Anchored to the top-right of the frame for a consistent popover feel. */
            className="fixed top-4 right-4 w-[min(22rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto bg-card border border-border rounded-2xl z-[70] fade-up"
            style={{ boxShadow: "var(--shadow-floating)" }}
            data-testid="notifications-panel"
          >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
            <div>
              <div className="font-display text-base font-medium">Activity</div>
              <div className="text-[11px] text-muted-foreground">
                {data.unread} unread · {data.count} recent
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/activity"); }}
              data-testid="see-all-notifications"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              See all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {data.notifications.length === 0 ? (
            <div className="px-4 py-10 text-sm text-muted-foreground text-center">
              All clear. No recent activity.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {data.notifications.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const clickable = !!n.link;
                return (
                  <li
                    key={n.id}
                    onClick={() => openNotification(n)}
                    className={`px-4 py-3 transition-colors ${clickable ? "cursor-pointer hover:bg-secondary/40" : ""} ${!n.read ? "bg-secondary/20" : ""}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${SEVERITY_TONES[n.severity] || SEVERITY_TONES.low} ${n.read ? "opacity-50" : ""}`}>
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm leading-tight ${n.read ? "font-normal text-muted-foreground" : "font-medium"}`}>
                          {n.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{relativeTime(n.created_at)}</div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        </>
      )}
    </div>
  );
}
